import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Upload, FileText, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/services/api';
import ManualLabEntryForm from './ManualLabEntryForm';

interface LabUploadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  patientId: string;
  clinicId: string;
  onUploadComplete?: (jobId: string) => void;
}

const LabUploadDialog: React.FC<LabUploadDialogProps> = ({
  open,
  onOpenChange,
  patientId,
  clinicId,
  onUploadComplete,
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<'idle' | 'uploading' | 'processing' | 'complete' | 'error'>('idle');
  const [showManualEntry, setShowManualEntry] = useState(false);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      const validTypes = [
        'application/pdf',
        'image/jpeg',
        'image/png',
        'image/webp',
        'image/heic',
      ];

      if (!validTypes.includes(file.type)) {
        toast.error('Invalid file type. Please upload a PDF or image file.');
        return;
      }

      // Validate file size (15MB max)
      const maxSize = 15 * 1024 * 1024;
      if (file.size > maxSize) {
        toast.error('File too large. Maximum size is 15MB.');
        return;
      }

      setSelectedFile(file);
      setUploadStatus('idle');
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    try {
      setUploading(true);
      setUploadStatus('uploading');

      // Step 1: Get presigned URL
      const uploadUrlResponse = await api.post('/uploads/lab-result', {
        clinicId,
        patientId,
        filename: selectedFile.name,
        contentType: selectedFile.type,
      });

      const { uploadUrl, key } = uploadUrlResponse.data;

      // Step 2: Upload file to S3
      await fetch(uploadUrl, {
        method: 'PUT',
        body: selectedFile,
        headers: {
          'Content-Type': selectedFile.type,
        },
      });

      toast.success('File uploaded successfully');
      setUploadStatus('processing');

      // Step 3: Create extraction job
      const jobResponse = await api.post('/labs/extractions/queue', {
        patientId,
        clinicId,
        s3Key: key,
        fileName: selectedFile.name,
        fileType: selectedFile.type,
        fileSize: selectedFile.size,
        sourceContext: 'clinic_upload',
      });

      const jobId = jobResponse.data.job.id;

      toast.success('Lab results uploaded — AI is extracting values...', {
        description: 'You can review the results once extraction is complete.',
      });

      setUploadStatus('complete');

      // Notify parent and close
      if (onUploadComplete) {
        onUploadComplete(jobId);
      }

      // Close dialog after brief delay
      setTimeout(() => {
        onOpenChange(false);
        resetDialog();
      }, 2000);
    } catch (error: any) {
      toast.error('Upload failed', {
        description: error.response?.data?.message || error.message || 'Please try again',
      });
      setUploadStatus('error');
    } finally {
      setUploading(false);
    }
  };

  const resetDialog = () => {
    setSelectedFile(null);
    setUploadStatus('idle');
    setShowManualEntry(false);
  };

  const handleClose = () => {
    if (!uploading) {
      onOpenChange(false);
      resetDialog();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Upload Lab Results</DialogTitle>
          <DialogDescription>
            Upload a PDF or image of lab results. AI will extract biomarker values automatically.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* File input */}
          <div className="space-y-2">
            <label
              htmlFor="lab-file"
              className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
            >
              {selectedFile ? (
                <div className="flex flex-col items-center">
                  <FileText className="h-8 w-8 text-[#00A79D] mb-2" />
                  <span className="text-sm font-medium">{selectedFile.name}</span>
                  <span className="text-xs text-gray-500">
                    {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                  </span>
                </div>
              ) : (
                <div className="flex flex-col items-center">
                  <Upload className="h-8 w-8 text-gray-400 mb-2" />
                  <span className="text-sm text-gray-600">Click to select file</span>
                  <span className="text-xs text-gray-400 mt-1">PDF, JPEG, PNG, WebP, HEIC (max 15MB)</span>
                </div>
              )}
              <input
                id="lab-file"
                type="file"
                className="hidden"
                accept=".pdf,.jpg,.jpeg,.png,.webp,.heic"
                onChange={handleFileSelect}
                disabled={uploading}
              />
            </label>
          </div>

          {/* Status indicator */}
          {uploadStatus !== 'idle' && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-gray-50">
              {uploadStatus === 'uploading' && (
                <>
                  <Loader2 className="h-4 w-4 animate-spin text-[#00A79D]" />
                  <span className="text-sm">Uploading file...</span>
                </>
              )}
              {uploadStatus === 'processing' && (
                <>
                  <Loader2 className="h-4 w-4 animate-spin text-[#00A79D]" />
                  <span className="text-sm">Starting AI extraction...</span>
                </>
              )}
              {uploadStatus === 'complete' && (
                <>
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <span className="text-sm text-green-700">Upload complete!</span>
                </>
              )}
              {uploadStatus === 'error' && (
                <>
                  <AlertCircle className="h-4 w-4 text-red-600" />
                  <span className="text-sm text-red-700">Upload failed</span>
                </>
              )}
            </div>
          )}

          {/* Action buttons */}
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={uploading}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpload}
              disabled={!selectedFile || uploading}
              className="flex-1 bg-[#00A79D] hover:bg-[#008c85]"
            >
              {uploading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Upload
                </>
              )}
            </Button>
          </div>

          {/* Manual entry option */}
          {!showManualEntry && (
            <div className="text-center pt-2">
              <Button
                variant="link"
                size="sm"
                className="text-[#633990]"
                onClick={() => setShowManualEntry(true)}
                disabled={uploading}
              >
                Enter lab values manually instead
              </Button>
            </div>
          )}
        </div>

        {showManualEntry && (
          <div className="border-t pt-4">
            <ManualLabEntryForm
              patientId={patientId}
              onSave={async (tests, metadata) => {
                await api.post('/labs/extractions/manual-entry', {
                  patientId,
                  clinicId,
                  tests,
                  collectionDate: metadata.collectionDate,
                  labFacility: metadata.provider,
                });
                onUploadComplete?.('manual');
                onOpenChange(false);
                resetDialog();
              }}
              onCancel={() => setShowManualEntry(false)}
            />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default LabUploadDialog;
